import { Key } from 'react'
import { Item, Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'

import Styles from '../../ProfileDropdown.module.scss'

import { useFeatureFlagUserGroup } from 'hooks/useFeatureFlagUserGroup'
import { useCanViewAs } from 'components/Can'
import { useAuth } from 'hooks/UseAuth'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

import { USERS, checkFeatureFlagUserGroup } from 'utils/featureFlagUserGroups'

import { getInitials } from 'utils'

import Avatar from 'common/Avatar/Avatar'
import { Menu } from 'components/Menu/Menu'
import { Flag } from 'almanac-ui/icons'
import { ChevronRight } from 'blaze-ui/icons/24'
import {
  Card,
  UsersProfiles1,
  Logout1,
  Gift01,
  HelpCenterIcon,
  SupportBubble,
  BugIcon,
  Users,
  ArrowRefresh,
} from 'blaze-ui/icons'
import { Settings } from 'blaze-ui/icons/20/Settings'
import { Workspace, UserRole, SettingsMember } from 'types'
import { WithAutopilotUpgradeBadge } from 'components/AutopilotUpgradeButtons/WithAutopilotUpgradeBadge'
import { useAPMobileWeb } from 'hooks/useAPMobileWeb'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useRootStore } from 'stores/RootStore'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { REACT_APP_BETA_ENVIRONMENT } from 'env'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { CreditUsage } from './components/CreditUsage/CreditUsage'
import { AutoReload } from './components/AutoReload/AutoReload'

export const MainMenu = ({
  onAction,
  workspace,
  profile,
}: {
  onAction: (key: Key) => void
  workspace: Workspace
  profile?: SettingsMember
}) => {
  // Not using the useIsSystemRedesign hook because it would cause lots of tests that are not wrapped in redux Providers and that render menus to fail
  const isSystemRedesignEnabled = document.documentElement.getAttribute('data-autopilot-system-redesign') === 'on'
  const canOverrideFlipper = useFeatureFlagUserGroup([USERS.withAlmanacEmail])
  const canViewAs = useCanViewAs(workspace.id)
  const auth = useAuth()
  const isAlmanacUser = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], auth?.email)
  const { isTabletPortrait } = useResponsiveDesign()

  const isAPMobileWeb = useAPMobileWeb()
  const showReleaseInfo = useFlipperFlag('show_release_info')

  const desktopIconSize = isSystemRedesignEnabled ? 16 : undefined
  const iconSize = isAPMobileWeb ? 20 : desktopIconSize
  const avatarBgColor = isSystemRedesignEnabled ? '#dfdfdf' : 'var(--avatar)'
  const {
    creditUsageStore: { creditUsage },
    tierUsageStore: { currentSubscription },
  } = useRootStore()
  const isPioneerTrialing =
    currentSubscription?.plan === PaymentPlanKey.VirtualMarketer && currentSubscription?.status === 'trialing'
  const showCreditUsage =
    workspace.is_virtual_marketer && creditUsage && Object.keys(creditUsage).length > 0 && !isPioneerTrialing
  const showAutoReload = showCreditUsage && currentSubscription?.status !== 'trialing'

  return (
    <div>
      <Menu
        aria-label="Workspaces"
        itemClassName={Styles.menuItem}
        onAction={onAction}
        selectionMode="single"
        selectedKeys={new Set([`workspace-${workspace.id}`])}
        showCheckmark={false}
      >
        <Section>
          {
            [
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                <Item key="profile" textValue="Profile">
                  <div className={Styles.selectedWorkspace}>
                    {profile?.avatar_url ? (
                      <div className={Styles.profileButtonContainer}>
                        <img className={Styles.profileButton} src={profile.avatar_url} alt="user profile" />
                        <Avatar
                          className={Styles.avatar}
                          label={getInitials(workspace.name)}
                          size={isAPMobileWeb ? 47 : 20}
                          workspaceId={workspace.id}
                          url={workspace.logo_url}
                          backgroundColor={avatarBgColor}
                        />
                      </div>
                    ) : (
                      <Avatar
                        label={getInitials(workspace.name)}
                        size={isAPMobileWeb ? 47 : 32}
                        workspaceId={workspace.id}
                        url={workspace.logo_url}
                        backgroundColor={avatarBgColor}
                      />
                    )}
                    {isAPMobileWeb ? (
                      <div className={Styles.selectedWorkspaceInfo}>
                        <Heading level={5}>{profile?.display_name}</Heading>
                        <Text variant="label" fontWeight={'400'}>
                          {profile?.email}
                        </Text>
                      </div>
                    ) : (
                      <div className={Styles.selectedWorkspaceInfo}>
                        <span className={Styles.selectedWorkspaceName}>{profile?.display_name}</span>
                        <span className={Styles.selectedWorkspaceMembers}>{profile?.email}</span>
                      </div>
                    )}
                  </div>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && showCreditUsage && (
                <Item key="creditUsage" textValue="Credit Usage">
                  <CreditUsage className={Styles.creditUsageContainer} />
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && showAutoReload && (
                <Item key="autoReload" textValue="Auto-reload">
                  <AutoReload className={Styles.autoReloadContainer} />
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                <Item key="settings" textValue="Settings">
                  <Settings size={iconSize} /> Settings
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                <Item key="billing" textValue="Your Billing">
                  <Card size={iconSize} /> Your Billing
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.GUEST]) && (
                <Item key="switchWorkspace" textValue="Switch Workspace">
                  <UsersProfiles1 size={iconSize} />
                  Switch Workspace
                  <div className={Styles.switchWorkspaceChevronRight}>
                    {isAPMobileWeb ? null : <ChevronRight size={16} />}
                  </div>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && !isAPMobileWeb && (
                <Item key="joinCommunity" textValue="Join Community">
                  <Users />
                  Join Our Community
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && !isAPMobileWeb && (
                <Item key="referAndEarn" textValue="Refer And Earn">
                  <Gift01 size={iconSize} />
                  Refer & Earn
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                <Item key="helpCenter" textValue="Help Center">
                  <HelpCenterIcon size={iconSize} />
                  Help Center
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) &&
                !isAPMobileWeb &&
                isAlmanacUser &&
                !isTabletPortrait && (
                  <Item key="bugReporter" textValue="Report Bug">
                    <BugIcon size={iconSize} />
                    Report Bug
                  </Item>
                ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && !isAPMobileWeb && (
                <Item key="intercomControl" textValue="Support" aria-label="Support">
                  <WithAutopilotUpgradeBadge>
                    <SupportBubble size={iconSize} />
                    Support
                  </WithAutopilotUpgradeBadge>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              isAlmanacUser && showReleaseInfo && isAPMobileWeb && (
                <Item key="releaseInfo" textValue="Release Info">
                  <ArrowRefresh size={iconSize} />
                  {`Refresh (Current: ${REACT_APP_BETA_ENVIRONMENT ? 'Beta' : 'Main'})`}
                </Item>
              ),
              canOverrideFlipper && (
                <Item key="featureFlags" textValue="Feature flags">
                  <Flag size={iconSize} /> Feature flags
                </Item>
              ),
              <Item key="signout" textValue="Sign out">
                <Logout1 size={iconSize} /> Sign out
              </Item>,
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
      </Menu>
    </div>
  )
}
